import { useState, useEffect, type ReactNode } from 'react'
import { useSidebarTabs, type SidebarTab } from '@/contexts/SidebarContext'

interface Tab {
  id: string
  label: string
  badge?: number
  content: ReactNode
  /** Scrollbar im Tab-Panel unterdrücken (z.B. für Übersicht) */
  noScroll?: boolean
}

interface Props {
  tabs: Tab[]
  defaultTab?: string
  /** Controlled mode: active tab id */
  activeTab?: string
  /** Controlled mode: called when user clicks a tab */
  onTabChange?: (id: string) => void
  /** Titel über den Tabs in der Sidebar */
  sectionTitle?: string
}

export default function Tabs({ tabs, defaultTab, activeTab, onTabChange, sectionTitle }: Props) {
  const [internal, setInternal] = useState(defaultTab ?? tabs[0]?.id)
  const active = activeTab ?? internal
  const setActive = onTabChange ?? setInternal
  const { register, unregister } = useSidebarTabs()

  // defaultTab kann sich durch Navigation ändern (z.B. Termine-Link)
  useEffect(() => {
    if (defaultTab && !activeTab) setInternal(defaultTab)
  }, [defaultTab, activeTab])

  // Tabs in der Sidebar registrieren
  useEffect(() => {
    const sidebarTabs: SidebarTab[] = tabs.map(({ id, label, badge }) => ({ id, label, badge }))
    register(sidebarTabs, active, setActive, sectionTitle)
  }, [tabs.length, active, setActive, sectionTitle, register])

  // Bei Unmount deregistrieren
  useEffect(() => {
    return () => unregister()
  }, [unregister])

  return (
    <>
      {/* Nur Panel-Content rendern – Navigation ist in der Sidebar */}
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          className={active === tab.id
            ? `flex flex-col flex-1 min-h-0${tab.noScroll ? '' : ' overflow-y-auto'}`
            : 'hidden'
          }
        >
          {tab.content}
        </div>
      ))}
    </>
  )
}
